import React, { useState, useEffect, useRef } from 'react';
import './VenueEditorGuide.css';

const SECTIONS = [
  { id: 'overview',     icon: '🗺',  title: 'Overview' },
  { id: 'venue-list',   icon: '📋',  title: 'Venue List' },
  { id: 'profile',      icon: '✏️',  title: 'Venue Profile' },
  { id: 'happy-hours',  icon: '🍺',  title: 'Happy Hours' },
  { id: 'events',       icon: '📅',  title: 'Events' },
  { id: 'specials',     icon: '🍽',  title: 'Daily Specials' },
  { id: 'preview',      icon: '👁',  title: 'Preview' },
  { id: 'new-venue',    icon: '➕',  title: 'New Venue' },
  { id: 'tips',         icon: '💡',  title: 'Tips & Tricks' },
];

function Field({ name, hint, required }) {
  return (
    <div className="veg-field">
      <span className="veg-field-name">{name}{required && <span className="veg-field-req"> *</span>}</span>
      <span className="veg-field-hint">{hint}</span>
    </div>
  );
}

function Step({ n, children }) {
  return (
    <div className="veg-step">
      <span className="veg-step-n">{n}</span>
      <span className="veg-step-text">{children}</span>
    </div>
  );
}

function Tip({ children }) {
  return <div className="veg-tip">💡 {children}</div>;
}

function Note({ children }) {
  return <div className="veg-note">ℹ️ {children}</div>;
}

function SectionHead({ id, icon, title }) {
  return (
    <div className="veg-section-head" id={`veg-${id}`}>
      <span className="veg-section-icon">{icon}</span>
      <h2 className="veg-section-title">{title}</h2>
    </div>
  );
}

export default function VenueEditorGuide({ onClose }) {
  const [activeSection, setActiveSection] = useState('overview');
  const contentRef = useRef(null);

  // Highlight nav item based on scroll position
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const handler = () => {
      const scrollTop = el.scrollTop + 80;
      let current = SECTIONS[0].id;
      for (const s of SECTIONS) {
        const target = document.getElementById(`veg-${s.id}`);
        if (target && target.offsetTop <= scrollTop) current = s.id;
      }
      setActiveSection(current);
    };
    el.addEventListener('scroll', handler, { passive: true });
    return () => el.removeEventListener('scroll', handler);
  }, []);

  const scrollTo = (id) => {
    const target = document.getElementById(`veg-${id}`);
    if (target && contentRef.current) {
      contentRef.current.scrollTo({ top: target.offsetTop - 16, behavior: 'smooth' });
    }
    setActiveSection(id);
  };

  return (
    <div className="veg-backdrop" onClick={onClose}>
      <div className="veg-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="veg-header">
          <div className="veg-header-left">
            <span className="veg-header-icon">📚</span>
            <div>
              <div className="veg-header-title">Venue Editor Guide</div>
              <div className="veg-header-sub">Everything you need to manage venue content</div>
            </div>
          </div>
          <button className="veg-close" onClick={onClose}>✕</button>
        </div>

        {/* Body: nav + content */}
        <div className="veg-body">

          {/* Sidebar nav */}
          <nav className="veg-nav">
            {SECTIONS.map(s => (
              <button
                key={s.id}
                className={`veg-nav-item ${activeSection === s.id ? 'active' : ''}`}
                onClick={() => scrollTo(s.id)}
              >
                <span className="veg-nav-icon">{s.icon}</span>
                <span className="veg-nav-label">{s.title}</span>
              </button>
            ))}
          </nav>

          {/* Scrollable content */}
          <div className="veg-content" ref={contentRef}>

            {/* ── Overview ── */}
            <section className="veg-section">
              <SectionHead id="overview" icon="🗺" title="Overview" />
              <p className="veg-p">
                The <strong>Venue Editor</strong> gives admins a single place to browse every venue
                in the market and directly create or edit their happy hours, events, and daily
                specials — all changes write straight to Airtable in real time, no review queue needed.
              </p>
              <Note>Only admins and super admins can access the Venue Editor. It respects whichever market is active in the top market switcher.</Note>
              <p className="veg-p">Find it in the Admin Console, 3rd section down — <strong>🏪 Venue Editor</strong>. Click the header to expand it.</p>
              <div className="veg-feature-grid">
                <div className="veg-feature-card">
                  <span>📋</span><span>Browse all venues with content health at a glance</span>
                </div>
                <div className="veg-feature-card">
                  <span>✏️</span><span>Edit profiles, deals, events &amp; specials in one drawer</span>
                </div>
                <div className="veg-feature-card">
                  <span>👁</span><span>Preview exactly how content will look on the live site</span>
                </div>
              </div>
            </section>

            {/* ── Venue List ── */}
            <section className="veg-section">
              <SectionHead id="venue-list" icon="📋" title="Venue List" />
              <p className="veg-p">The main list shows every venue in your market. Each row displays:</p>
              <ul className="veg-list">
                <li><strong>Thumbnail</strong> — venue logo or placeholder initial</li>
                <li><strong>Name + description</strong> — truncated to one line</li>
                <li><strong>Content badges</strong> — <span className="veg-badge veg-badge-green">HH 3</span> happy hours · <span className="veg-badge veg-badge-green">EVT 2</span> events · <span className="veg-badge veg-badge-green">SPL 1</span> specials. Grey = none.</li>
                <li><strong>Edit button</strong> — opens the full edit drawer</li>
              </ul>

              <h3 className="veg-h3">Filters</h3>
              <p className="veg-p">Filter pills at the top narrow the list. Filters stack — you can combine them.</p>
              <div className="veg-filter-demo">
                <span className="veg-pill veg-pill-active">Active</span>
                <span className="veg-pill">Has Events</span>
                <span className="veg-pill">Has HH</span>
                <span className="veg-pill">Has DS</span>
              </div>
              <ul className="veg-list">
                <li><strong>All</strong> — clears all filters, shows every venue</li>
                <li><strong>Active</strong> — default; hides inactive/archived venues</li>
                <li><strong>Has Events / Has HH / Has DS</strong> — show only venues that have content in that category</li>
              </ul>
              <Tip>Combine "Active" + "Has HH" to quickly find live venues that already have happy hour deals.</Tip>

              <h3 className="veg-h3">Search</h3>
              <p className="veg-p">Type in the search bar to filter by venue name instantly (no server request needed).</p>
            </section>

            {/* ── Profile ── */}
            <section className="veg-section">
              <SectionHead id="profile" icon="✏️" title="Venue Profile" />
              <p className="veg-p">Click <strong>Edit</strong> on any venue row to open the drawer, then select the <strong>Profile</strong> tab.</p>

              <h3 className="veg-h3">Thumbnail Image</h3>
              <p className="veg-p">Click <strong>Upload image</strong> (or <strong>Replace image</strong>) to swap the venue's logo. Uploads are sent directly to Airtable's attachment field.</p>

              <h3 className="veg-h3">Profile Fields</h3>
              <Field name="Venue Name" hint="Shown on all public cards. Admin-only — venue owners cannot change this." required />
              <Field name="Type" hint="Single select: American, Asian, Bar, Brewery, Café, Indian, Italian, Mexican, Pizza, Pub, Restaurant, Steakhouse, Winery." required />
              <Field name="City" hint="Used for city-based filtering on the consumer site." required />
              <Field name="Address" hint="Displayed on the venue's expanded card." />
              <Field name="Phone" hint="Silver+ venues show a clickable phone button on their public card." />
              <Field name="Website" hint="Linked from the venue card footer." />
              <Field name="Facebook URL" hint="Shows a Facebook icon on Silver+ venue cards." />
              <Field name="Instagram URL" hint="Shows an Instagram icon on Silver+ venue cards." />
              <Field name="Featured" hint="Checkbox — featured venues appear with a badge and sort higher." />

              <Note>Tier and Priority fields are visible to super admins only and control sort order and card styling.</Note>
              <p className="veg-p">Click <strong>Save Profile</strong> when done. Changes go live immediately.</p>
            </section>

            {/* ── Happy Hours ── */}
            <section className="veg-section">
              <SectionHead id="happy-hours" icon="🍺" title="Happy Hours" />
              <p className="veg-p">The <strong>Happy Hours</strong> tab lists all happy hour deals linked to this venue. Each deal is a single item or group of items offered at a specific time.</p>

              <h3 className="veg-h3">Adding a Deal</h3>
              <Step n={1}>Click <strong>+ Add Deal</strong>.</Step>
              <Step n={2}>Fill in the fields (see below).</Step>
              <Step n={3}>Add items using the item rows.</Step>
              <Step n={4}>Click <strong>Preview</strong> to see how it will look, then click <strong>Save</strong>.</Step>

              <h3 className="veg-h3">Deal Fields</h3>
              <Field name="Deal Name" hint="Short, SPECIFIC label that becomes the card header — e.g. '$5 Drafts', 'Late Night Wings', 'Half-Price Apps'. Avoid generic names like 'Happy Hour' or 'Specials'." />
              <Field name="Display Title" hint="Toggle next to the Deal Name field. When checked, the deal name is shown as a bold header above the item list on the public site. Uncheck to hide it — useful when the name is self-explanatory from the items." />
              <Field name="Days of Week" hint="Select which days this deal runs. Tap individual days or use the All shortcut to select every day at once. Leave blank and it will show as 'Daily'." />
              <Field name="Start Time / End Time" hint="Use 12-hour format, e.g. 3:00 PM. The site displays the range as '3:00 – 6:00 PM'." />
              <Field name="Recurring" hint="Check this for any deal that repeats on a regular schedule (almost always true)." />
              <Field name="Recurrence Description" hint="Only appears when Recurring is checked. Only fill this for non-standard schedules, e.g. '1st and 3rd Monday of every month'. Leave blank for standard weekly deals." />
              <Field name="Items" hint="One row per item — enter the item name, price, and an optional short description. Click + Add Item to add more rows. Use ↺ Reset from saved text to re-parse the underlying description if rows look wrong." required />
            </section>

            {/* ── Events ── */}
            <section className="veg-section">
              <SectionHead id="events" icon="📅" title="Events" />
              <p className="veg-p">The <strong>Events</strong> tab manages all events linked to the venue — both one-time and recurring.</p>

              <h3 className="veg-h3">Adding an Event</h3>
              <Step n={1}>Click <strong>+ Add Event</strong>.</Step>
              <Step n={2}>Enter the event name and select a type.</Step>
              <Step n={3}>Toggle <strong>Recurring</strong> if the event repeats on a schedule, or leave it off for a one-time event.</Step>
              <Step n={4}>Fill in dates/times and description.</Step>
              <Step n={5}>Optionally upload an image.</Step>
              <Step n={6}>Click <strong>Save</strong>.</Step>

              <h3 className="veg-h3">Event Fields</h3>
              <Field name="Event Name" hint="Displayed as the card headline." required />
              <Field name="Event Type" hint="Live Music, Trivia, Karaoke, Comedy, Open Mic, Sports, Special Event, Holiday, DJ, Bingo. Controls the default category image." />
              <Field name="Recurring" hint="Toggle on for weekly/monthly events. Toggle off for one-time events with a specific date." />
              <Field name="Days of Week" hint="(Recurring only) Which days the event occurs each week." />
              <Field name="Recurrence Description" hint="(Recurring only) Human-readable pattern, e.g. '1st and 3rd Thursday of every month'. Used for display and for smart date filtering." />
              <Field name="Start Date" hint="(One-time only) The date this event occurs, in YYYY-MM-DD format." />
              <Field name="End Date" hint="(One-time only, optional) For multi-day events — the last day of the event." />
              <Field name="Start Time / End Time" hint="Event time range in 12-hour format." />
              <Field name="Description" hint="Details about the event — bands, rules, ticket info, etc." />
              <Field name="Event Image" hint="Upload a custom photo. If none is uploaded, the site uses the category default image for the event type." />

              <h3 className="veg-h3">Event Images</h3>
              <p className="veg-p">The image thumbnail is shown in the event list row. In the edit form, you'll see the current image (or the category default) with an <strong>Add image / Change image</strong> button.</p>
              <Note>If you upload an image, it becomes the primary image. If you leave it blank, the site falls back to the event type's default image (e.g. a music graphic for Live Music events).</Note>
            </section>

            {/* ── Specials ── */}
            <section className="veg-section">
              <SectionHead id="specials" icon="🍽" title="Daily Specials" />
              <p className="veg-p">The <strong>Daily Specials</strong> tab manages recurring food or drink specials tied to specific days — things like Taco Tuesdays, Fish Fry Fridays, and Wing Wednesdays.</p>

              <h3 className="veg-h3">Adding a Special</h3>
              <Step n={1}>Click <strong>+ Add Special</strong>.</Step>
              <Step n={2}>Enter a name and select the days it runs.</Step>
              <Step n={3}>Optionally add start/end times if the special is time-limited.</Step>
              <Step n={4}>Add items using the item rows.</Step>
              <Step n={5}>Click <strong>Preview</strong> to check how it will look, then click <strong>Save</strong>.</Step>

              <h3 className="veg-h3">Special Fields</h3>
              <Field name="Special Name" hint="A specific, memorable name — e.g. 'Taco Tuesday', 'Fish Fry Friday', 'Wing Wednesday'. Avoid generic names like 'Daily Special' or 'Special'." />
              <Field name="Display Title" hint="Toggle next to the Special Name field. When checked, the special name is shown as a bold header above the item list. Uncheck to hide it." />
              <Field name="Days of Week" hint="Which days this special runs. Tap individual days or use the All shortcut to select every day at once." />
              <Field name="Start Time / End Time" hint="Optional — fill in if the special is only available during a specific window." />
              <Field name="Recurring" hint="Check this for specials that repeat every week." />
              <Field name="Recurrence Description" hint="Only appears when Recurring is checked. Only needed for non-standard schedules, e.g. 'Every other Friday'." />
              <Field name="Items" hint="One row per item — enter the item name, price, and an optional short description. Click + Add Item to add more rows. Use ↺ Reset from saved text to re-parse the underlying description if rows look wrong." required />
            </section>

            {/* ── Preview ── */}
            <section className="veg-section">
              <SectionHead id="preview" icon="👁" title="Preview" />
              <p className="veg-p">There are two Preview buttons in the Venue Editor:</p>

              <h3 className="veg-h3">Tab Preview (saved content)</h3>
              <p className="veg-p">Each content tab (Happy Hours, Events, Daily Specials) has a <strong>Preview</strong> button in its header. This opens a full modal showing all <em>saved</em> content for that venue rendered exactly as it will appear on the consumer site.</p>

              <h3 className="veg-h3">Edit Form Preview (unsaved changes)</h3>
              <p className="veg-p">When editing a deal or special, the form footer also has a <strong>Preview</strong> button. This shows your <em>current unsaved changes</em> — so you can verify how items will look before committing. The edit form stays open behind the preview; close the preview to return to editing.</p>
              <Note>Events do not have a form-level Preview button — use the tab Preview to check events.</Note>

              <h3 className="veg-h3">Deals Preview</h3>
              <p className="veg-p">Deals are grouped by their schedule (same days + time = one block). At the top you'll see category filter tabs matching the live site:</p>
              <div className="veg-filter-demo">
                <span className="veg-pill veg-pill-active">All</span>
                <span className="veg-pill">🍺 Drinks</span>
                <span className="veg-pill">🍽 Food</span>
                <span className="veg-pill">✨ Other</span>
              </div>
              <p className="veg-p">Each deal renders its items as a name/price list, exactly as a visitor would see them.</p>

              <h3 className="veg-h3">Events Preview</h3>
              <p className="veg-p">Events render with a full-width image on top, followed by the event name, description, type badge, and date/time — matching the mobile layout of the live site.</p>

              <h3 className="veg-h3">Specials Preview</h3>
              <p className="veg-p">Specials are grouped by day label (Monday, Weekdays, Daily, etc.). Each special shows its name as a header with items listed below in name/price format.</p>

              <Tip>Use the edit form Preview to check unsaved changes, then use the tab Preview as a final check of all saved content before closing the drawer.</Tip>
            </section>


            {/* ── New Venue ── */}
            <section className="veg-section">
              <SectionHead id="new-venue" icon="➕" title="New Venue" />
              <p className="veg-p">Click <strong>+ New Venue</strong> in the top-right of the Venue Editor to create a brand new venue record.</p>

              <h3 className="veg-h3">Required Fields</h3>
              <Field name="Venue Name" hint="The name as it will appear on the site." required />
              <Field name="Type" hint="Category of the venue — American, Bar, Brewery, etc." required />
              <Field name="City" hint="The city where the venue is located." required />

              <h3 className="veg-h3">Optional Fields at Creation</h3>
              <Field name="Address" hint="Full street address." />
              <Field name="Description" hint="A short description of the venue." />
              <Field name="Phone" hint="Contact phone number." />
              <Field name="Website" hint="The venue's website URL." />

              <Note>New venues are set to Active by default and go live on the consumer site immediately. Add a thumbnail and content right away for the best first impression.</Note>
              <p className="veg-p">After clicking <strong>Create Venue</strong>, the drawer automatically opens on the new venue so you can add deals, events, and specials right away.</p>
            </section>

            {/* ── Tips ── */}
            <section className="veg-section">
              <SectionHead id="tips" icon="💡" title="Tips & Tricks" />

              <h3 className="veg-h3">Adding Items to a Deal or Special</h3>
              <p className="veg-p">Deals and specials use a structured item editor — no need to type formatted text. Each row has three fields:</p>
              <ul className="veg-list">
                <li><strong>Item Name</strong> — what the item is, e.g. "Draft Beer", "House Margarita", "Wings"</li>
                <li><strong>Price</strong> — just the number or amount, e.g. "$5" or "Half Off". Leave blank if there's no price.</li>
                <li><strong>Description</strong> — optional supporting detail, e.g. "excludes seasonal taps", "Tuesdays only"</li>
              </ul>
              <p className="veg-p">Click <strong>+ Add Item</strong> to add more rows. Use the <strong>Preview</strong> button in the form footer to check how items will look before saving.</p>

              <h3 className="veg-h3">✦ Convert with AI</h3>
              <p className="veg-p">This is the current AI feature available in the Venue Editor. When editing a Happy Hour deal or Daily Special that has a free-text description the parser couldn't read into structured items, a yellow <strong>⚠️ warning banner</strong> appears below the item rows.</p>
              <p className="veg-p">You'll see this most often on venues with scraped or imported descriptions. Two ways to fix it:</p>
              <ul className="veg-list">
                <li><strong>✦ Convert with AI</strong> — click the purple button in the banner. The AI reads the raw description and populates the item rows automatically. Review the pre-filled rows, tweak anything that looks off, then Save.</li>
                <li><strong>Manual</strong> — fill in the item rows yourself. Once you save with at least one item row, the old description is replaced.</li>
              </ul>
              <Note>If you save with all item rows left blank, the original description is preserved as-is — nothing is lost.</Note>
              <Tip>Convert with AI works best on descriptions like "Draft beers $3, house wine $5, apps half off" — unstructured prose that clearly lists items and prices. It may need a little manual cleanup on very messy or ambiguous descriptions.</Tip>

              <h3 className="veg-h3">Overriding Item Category (D / F / O)</h3>
              <p className="veg-p">Each item row has three small buttons to the right of the price field: <strong>D</strong> = Drinks, <strong>F</strong> = Food, <strong>O</strong> = Other. Hover over any button to see its label. By default all are unset and the system auto-detects the category from keywords in the item name.</p>
              <p className="veg-p">Tap a button to force that item into a specific category tab on the consumer site. Tap the same button again to clear the override and return to auto-detection. This works per-item, so a single deal can have beer items in Drinks and wings in Food simultaneously.</p>
              <Tip>Use D/F/O when auto-detection gets an item wrong — e.g. "Margarita Flight" being put in Drinks when it should be Food. No need to touch the global keyword manager for one-off cases.</Tip>

              <h3 className="veg-h3">One Deal Per Item vs. One Deal Per Schedule</h3>
              <p className="veg-p">You can structure deals two ways:</p>
              <ul className="veg-list">
                <li><strong>One deal record per item</strong> — e.g. a separate record for "Draft Beer $5", "House Wine $4", etc. Cleaner categorization into Drinks/Food tabs on the consumer site, but more records to manage.</li>
                <li><strong>One deal record with multiple items</strong> — add all items as rows within a single deal. Fewer records and easier to manage.</li>
              </ul>
              <p className="veg-p">Either approach works — use whichever feels more natural for the venue.</p>

              <h3 className="veg-h3">Recurrence Description — When to Use It</h3>
              <p className="veg-p">Only fill in Recurrence Description for genuinely non-standard schedules:</p>
              <ul className="veg-list">
                <li>✅ "1st and 3rd Thursday of every month"</li>
                <li>✅ "Every other Friday"</li>
                <li>✅ "Last Sunday of the month"</li>
                <li>❌ "Every Monday–Friday" — just set Days of Week instead</li>
                <li>❌ "Every Saturday" — just set Days of Week instead</li>
              </ul>

              <h3 className="veg-h3">Event Types &amp; Images</h3>
              <p className="veg-p">Each event type maps to a default category image shown when no custom image is uploaded:</p>
              <div className="veg-type-grid">
                {['Live Music','Trivia','Karaoke','Comedy','Open Mic','Sports','Special Event','Holiday','DJ','Bingo'].map(t => (
                  <span key={t} className="veg-type-chip">{t}</span>
                ))}
              </div>
              <p className="veg-p">Always set the Event Type — it also controls which image shows in the consumer site event card header.</p>

            </section>

          </div>{/* end veg-content */}
        </div>{/* end veg-body */}
      </div>{/* end veg-modal */}
    </div>
  );
}
